import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { Repositories } from '@/infra/repositories';
import { type Response as CartShipmentsResponse } from '@/infra/services/checkout/cart/get-cart-shipments';
import { useGlobalStore } from '@/presenters/store/global';

type QueryOptions<TData = CartShipmentsResponse> = Omit<
  UseQueryOptions<CartShipmentsResponse, Error, TData>,
  'queryKey' | 'queryFn'
>;

export function useCartShipments<TData = CartShipmentsResponse>(
  options?: QueryOptions<TData>
) {
  const cartId = useGlobalStore((store) => store.session?.cartId);

  const query = useQuery<CartShipmentsResponse, Error, TData>({
    queryKey: ['cart-shipments', cartId],
    queryFn: async () => {
      if (!cartId) {
        throw new Error('Cart ID not found');
      }
      return await Repositories.Checkout.Cart.getCartShipments({ cartId });
    },
    enabled: !!cartId,
    cacheTime: 0,
    staleTime: 0,
    retry: 2,
    ...options,
  });

  return {
    ...query,
    isLoading: query.isLoading,
    error: query.error,
  };
}
